/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx, useTheme } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo } from 'react';
import Media from 'react-media';

import useRouter, { useLocation } from 'js/lib/useRouter';

import { Grid, PageGridContainer, breakpoints, useMediaQuery } from '@coursera/cds-core';

import AnnualUpgradeConfirmationModal from 'bundles/coursera-plus-common/components/monthlyToAnnualUpgrade/AnnualUpgradeConfirmationModal';
import { useUserAgent } from 'bundles/page/contexts/UserAgentApiContext';
import SearchNoResultsRecommendations from 'bundles/search-common/components/SearchNoResultsRecommendations';
import SearchPageBody from 'bundles/search-common/components/SearchPageBody';
import BrowseInSearch from 'bundles/search-common/components/experiments/browseInSerp/BrowseInSearch';
import AllSearchFiltersModal from 'bundles/search-common/components/experiments/filters2024/AllSearchFiltersModal';
import StickyFilterBar from 'bundles/search-common/components/experiments/filters2024/StickyFilterBar';
import ContextAwareFiltersButton from 'bundles/search-common/components/experiments/filters2024/components/ContextAwareFilterButton';
import ResetFiltersAndSortButton from 'bundles/search-common/components/experiments/filters2024/components/ResetFiltersAndSortButton';
import SearchPageFilters from 'bundles/search-common/components/filters/SearchPageFilters';
import SearchBar from 'bundles/search-common/components/searchbar/SearchBar';
import EntityPageFeaturedSnippet from 'bundles/search-common/components/seo/EntityPageFeaturedSnippet';
import ResultsGoogleSchemaMarkup from 'bundles/search-common/components/seo/ResultsGoogleSchemaMarkup';
import SearchSEOMetatagsWriter from 'bundles/search-common/components/seo/SearchSEOMetatagsWriter';
import { MAX_SCREEN_WIDTH_FOR_MOBILE_SEARCH_BAR, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { FILTER_TYPE, PAGINATION_TYPE } from 'bundles/search-common/constants/filter';
import type { FilterType } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchFacet } from 'bundles/search-common/providers/searchTypes';
import { isSearchTheme } from 'bundles/search-common/searchTheme';
import {
  getSearchEventingData,
  inferPageNumberFromParams,
  inferQueryFromParams,
} from 'bundles/search-common/utils/eventingUtils';
import {
  getShouldShowBrowseInSERP,
  getShouldShowProductCardDensityRatingWithImpression,
  getShouldShowProductCardDensitySkillsWithImpression,
  impressIntoFilterExperiment,
  isInFilterTestVariant,
} from 'bundles/search-common/utils/experimentUtils';
import useDefaultLanguageFilter from 'bundles/search-common/utils/useDefaultLanguageFilter';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import { getSearchUrlFromParams, hasLegacyQueryParams, isSearchProductHit } from 'bundles/search-common/utils/utils';
import useTrackQueryResults from 'bundles/search-v2/hooks/useTrackQueryResults';

const styles = {
  overflowShim: css`
    overflow-y: unset;
    overflow-x: unset;
  `,
  stickyFilterHeaderWrap: css`
    /* needed for sticky logic */
    height: 80px;
    position: relative;
    width: 100%;
  `,
  stickyFilterHeaderWrapMobileFixed: css`
    @media screen and (max-width: 1060px) {
      /* because currently filter always sticky below 1060px */
      height: 0;
    }
  `,
  spacingForStickyFilter: css`
    @media screen and (max-width: 1060px) {
      /* always sticky require padding so content not hidden below sticky */
      padding-top: 80px;
    }
  `,
  whiteBackground: css`
    background-color: var(--cds-color-neutral-background-primary);
  `,
  filterChipsWrapper: css`
    display: flex;
    align-items: center;

    ${breakpoints.down('sm')} {
      flex-direction: row-reverse;
    }
  `,
};

/**
 * Quick, hacky function as a temp shim for the filter placeholder UI
 * Intended be replaced by a more robust solution in the future
 */
export function getFilterCountFromParams(
  queryParams: Record<string, string | string[] | null | undefined>,
  facets: SearchFacet[] | null | undefined
): number {
  if (!queryParams || !facets) return 0;

  return facets?.reduce((acc, facet) => {
    let result = acc;
    // check if string or array;
    if (Array.isArray(queryParams[facet.name])) {
      // since array, add the length;
      result += queryParams[facet.name]!.length;
    } else if (queryParams[facet.name]) {
      // otherwise, if just exists we can assume it's a string;
      result += 1;
    }
    return result;
  }, 0);
}

export const SearchPageRoot = ({ isSEOEntityPage }: { isSEOEntityPage?: boolean }) => {
  // Setting language to English for folks in the EN locale is an important part of the filter redesign experiment!
  useDefaultLanguageFilter();
  const userAgent = useUserAgent();

  const isMobileOrTabletView = useMediaQuery(breakpoints.down('sm'), {
    defaultMatches: userAgent?.isMobileBrowser || userAgent?.isMobile,
  });
  const router = useRouter();
  const { query: queryParams } = useLocation();
  const { query: searchQueryFromParams, page: currentPageFromParams } = queryParams;
  const inferredPageNumberFromParams: string = inferPageNumberFromParams(currentPageFromParams);
  const inferredQueryFromParams: string = inferQueryFromParams(searchQueryFromParams);
  const theme = useTheme();
  const isConsumerSearchPage = useIsConsumerSerp();
  const { getSearchResults, isLoading } = useSearch();
  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const {
    query,
    facets,
    pagination,
    totalPages,
    facetFilters,
    source,
    hitsDisplayedCount = 0,
    hitsTotalCount = 0,
    inferredCurrentPageNumber = 0,
  } = searchData || {};
  const [showAllFiltersModal, setShowAllFiltersModal] = React.useState(false);
  const shouldShowBrowseInSERP = getShouldShowBrowseInSERP();

  const totalHits = pagination?.totalElements || undefined;
  const showNoResultsRecommendations = !isLoading && (totalHits || 0) < 1;
  const showSortBy = !isSEOEntityPage && !showNoResultsRecommendations;
  const activeFilters = useMemo(() => [...new Set(facetFilters?.flat())], [facetFilters]);
  const optimisticActiveFilterCount = useMemo(
    () => getFilterCountFromParams(queryParams, facets),
    [queryParams, facets]
  );

  // experiment impressions
  if (isConsumerSearchPage) {
    getShouldShowProductCardDensitySkillsWithImpression();
    getShouldShowProductCardDensityRatingWithImpression();
    impressIntoFilterExperiment();
  }

  const paginationType = isConsumerSearchPage ? PAGINATION_TYPE.INFINITE : PAGINATION_TYPE.PAGINATED;
  const isInfiniteStylePagination = paginationType === PAGINATION_TYPE.INFINITE;
  const shouldShowStickySidebarFilters =
    !showNoResultsRecommendations && isInfiniteStylePagination && !isMobileOrTabletView;

  // Determine filter type based on current conditions
  const filterType: FilterType = isConsumerSearchPage ? FILTER_TYPE.INLINE : FILTER_TYPE.EXPANDABLE;

  const eventingData = getSearchEventingData({
    source,
    inferredCurrentPageNumber,
    inferredPageNumberFromParams,
    isInfiniteStylePagination,
    activeFilters,
    searchQuery: inferredQueryFromParams,
  });
  useEffect(() => {
    // Redirect for legacy query params
    // Probably better solved in Edge, but this is the fix for now
    // Do we still need it? Probably: https://app.amplitude.com/analytics/coursera/chart/new/gwpjmdus
    if (hasLegacyQueryParams(queryParams)) {
      router.replace(getSearchUrlFromParams(queryParams));
    }
  }, [queryParams, facets, router]);

  useTrackQueryResults({ query, totalHits });

  if (!isSearchTheme(theme)) {
    throw new Error(
      'SearchPage can only be used in the context of a SearchTheme. Wrap this component with a ThemeProvider.'
    );
  }

  const inFilterTestVariantAndOnSearch = isInFilterTestVariant() && isConsumerSearchPage;
  const isOnLastPage =
    !isLoading && (inferredCurrentPageNumber >= (totalPages ?? 0) || hitsDisplayedCount === hitsTotalCount);

  const productSearchHits = useMemo(() => searchData?.elements.filter(isSearchProductHit), [searchData]);

  return (
    <main css={[theme.searchPageContainer, shouldShowStickySidebarFilters && styles.overflowShim]} id="main">
      <AnnualUpgradeConfirmationModal />
      {inFilterTestVariantAndOnSearch && (
        <AllSearchFiltersModal
          isOpen={showAllFiltersModal}
          onClose={() => setShowAllFiltersModal(false)}
          eventingData={eventingData}
        />
      )}
      <div
        css={[
          theme.mainContent,
          styles.whiteBackground,
          inFilterTestVariantAndOnSearch && !shouldShowBrowseInSERP && styles.spacingForStickyFilter,
        ]}
      >
        <PageGridContainer maxWidth="lg">
          <Media
            query={{ maxWidth: MAX_SCREEN_WIDTH_FOR_MOBILE_SEARCH_BAR }}
            defaultMatches={userAgent?.isMobileBrowser || userAgent?.isMobile}
          >
            {(matches) =>
              matches &&
              !isSEOEntityPage && (
                <div className="mobile-search-page-bar">
                  <SearchBar />
                </div>
              )
            }
          </Media>
          <BrowseInSearch eventingData={eventingData} />
          {inFilterTestVariantAndOnSearch && (
            <div
              css={[styles.stickyFilterHeaderWrap, !shouldShowBrowseInSERP && styles.stickyFilterHeaderWrapMobileFixed]}
            >
              <StickyFilterBar shouldAffixFilterOnMobile={!shouldShowBrowseInSERP}>
                <div css={styles.filterChipsWrapper}>
                  <ContextAwareFiltersButton
                    showLabel={!isMobileOrTabletView}
                    onClick={() => setShowAllFiltersModal(true)}
                  />
                </div>
                <ResetFiltersAndSortButton />
              </StickyFilterBar>
            </div>
          )}
          <Grid item xs={12}>
            <Grid container spacing={12}>
              {!inFilterTestVariantAndOnSearch && (
                <Grid item xs={12} md={3}>
                  {!showNoResultsRecommendations ? (
                    <SearchPageFilters
                      optimisticActiveFilterCount={optimisticActiveFilterCount}
                      eventingData={eventingData}
                      filterType={filterType}
                      showMobileSortBy={showSortBy}
                      shouldShowSectionTitleForInlineFilters={!isInfiniteStylePagination}
                    />
                  ) : null}
                </Grid>
              )}

              <Grid
                item
                xs={12}
                md={inFilterTestVariantAndOnSearch ? 12 : 9}
                css={shouldShowStickySidebarFilters && !isOnLastPage && { marginBottom: '500px' }}
              >
                {showNoResultsRecommendations ? (
                  <SearchNoResultsRecommendations eventingData={eventingData} />
                ) : (
                  <SearchPageBody
                    optimisticActiveFilterCount={optimisticActiveFilterCount}
                    isSEOEntityPageWithQuery={!!isSEOEntityPage}
                    eventingData={eventingData}
                    paginationType={paginationType}
                    searchQuery={inferredQueryFromParams}
                    page={Number(inferredPageNumberFromParams)}
                    showDesktopSortBy={showSortBy}
                    showRelatedQueries={true}
                    showResultsHeader={true}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
          <EntityPageFeaturedSnippet
            isSEOEntityPage={isSEOEntityPage}
            searchQuery={inferredQueryFromParams}
            searchResults={productSearchHits}
          />
        </PageGridContainer>
      </div>
      <ResultsGoogleSchemaMarkup hits={productSearchHits} />
      <SearchSEOMetatagsWriter hits={productSearchHits} totalPages={totalPages} />
    </main>
  );
};

export default SearchPageRoot;
